import React, { useState, useEffect } from "react";
import { Typography, Grid, Container } from "@mui/material";
import api from "../../globals/axios";
import LocateLand from "../../components/LocateLand";
import { useParams } from "react-router-dom";
import CustomAppBar from "../../components/AppBar";
import LoaderContainer from "../../components/LoaderContainer";
import DownLoadIcon from "../../assets/Icon/DownLoadIcon.png";
import ViewIcon from "../../assets/Icon/ViewIcon.png";
import { CustomTitle } from "../../components/Custom/CustomTypography";
import {
  CustomLabel,
  CustomValue,
} from "../../components/Custom/CustomTypography";
import { useTranslation } from "react-i18next";
import GenericButton from "../../components/Custom/GenericButton";
import { LoadScript } from "@react-google-maps/api";
import { GOOGLE_MAPS_API_KEY } from "../../globals/const";

export default function ViewCropDetails() {
  const { t } = useTranslation();
  const { cropId } = useParams();
  const [loading, setLoading] = useState(true);
  const [tokenData, setTokenData] = useState({});
  const [location, setLocation] = useState({});
  const [path, setPath] = useState([{ lat: 0, lng: 0 }]);
  const [selling, setSelling] = useState(false);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);

  const loadNFT = async () => {
    try {
      const res = await api.get(`/farmer/crops/${cropId}`);
      const data = res.data;
      setSelling(data.up_for_selling);
      setPath(data.polygon);
      setTokenData(data);
      setLocation({
        latitude: data.polygon[0]["lat"],
        longitude: data.polygon[0]["lng"],
      });
      setLoading(false);
    } catch (error) {
      console.error("Error loading farm data:", error);
      setLoading(false);
    }
  };

  const handleSell = async () => {
    try {
      setSelling(true);
      await api.patch(`/farmer/crops/${cropId}/`, { up_for_selling: true });
    } catch (error) {
      console.error("Error updating crop for selling:", error);
      setSelling(false);
    }
  };

  // Helper function to generate label-value pairs based on farm_type
  const getLabelValuePairs = (crop) => {
    if (!crop) return [];

    if (crop.farm_type === "CROP") {
      return [
        { label: t("Type"), value: "Crop" },
        { label: t("Land"), value: crop?.farmland?.farm_name ?? "" },
        { label: t("Crop Type"), value: crop?.commodity?.name ?? "" },
        {
          label: crop?.is_ratoon ? t("Start Date") : t("Sowing Date"),
          value: crop?.sowing_date ?? "",
        },
        { label: t("Seed Type"), value: crop?.seed_type ?? "NA" },
        { label: t("Seed Source"), value: crop?.seed_source ?? "NA" },
        {
          label:
            crop?.sowing_source === "SAPLING"
              ? t("Number of Saplings")
              : t("Seed Quantity (kg)"),
          value:
            crop?.sowing_source === "SAPLING"
              ? (crop?.sapling_quantity ?? "NA")
              : (crop?.seed_quantity ?? "NA"),
        },
        {
          label: t("Is EBC Contest"),
          value: crop?.is_ebc_contest ? "Yes" : "No",
        },
        {
          label: t("Is EBC Applied"),
          value: crop?.ebc_quantity > 0 ? "Yes" : "No",
        },
        {
          label: t("EBC Quantity"),
          value: crop?.ebc_quantity > 0 ? crop?.ebc_quantity : "NA",
        },
      ];
    } else {
      return [
        { label: t("Type"), value: "Plantation" },
        { label: t("Land"), value: crop?.farmland?.farm_name ?? "" },
        { label: t("Crop Type"), value: crop?.commodity?.name ?? "" },
        { label: t("Nursery Name"), value: crop?.nursery_name ?? "" },
        { label: t("Nursery Location"), value: crop?.nursery_location ?? "" },
        { label: t("Plantation Method"), value: crop?.plantation_method ?? "" },
        { label: t("Pit Size"), value: crop?.pit_size ?? "" },
        { label: t("Plant Count"), value: crop?.plant_count ?? "" },
        { label: t("Sowing Date"), value: crop?.sowing_date ?? "" },
      ];
    }
  };

  // Dynamically compute the data to display based on the crop type
  const labelValuePairs = getLabelValuePairs(tokenData);

  useEffect(() => {
    loadNFT();
  }, [cropId]);

  const managePlantation = () => {
    localStorage.setItem("farmerPlotId", cropId);
    window.location.href = `/schedule-activity/${cropId}`;
  };
  const handleFarmTraceActivities = () => {
    window.location.href = `/farmer/crop-trace-activities/?cropId=${cropId}&farmId=${tokenData?.farmland?.id}`;
  };

  return (
    <>
      <CustomAppBar />
      <LoaderContainer loading={loading}>
        <Container maxWidth="xs" sx={{ padding: "16px" }}>
          <CustomTitle>{t("Crop View")}</CustomTitle>
          <Grid
            container
            direction="column"
            style={{ marginTop: "10px" }}
            spacing={0}
          >
            {/* Display label-value pairs based on farm_type */}
            {labelValuePairs.map((pair) => (
              <Grid item container key={pair.label}>
                <Grid item xs={5}>
                  <CustomLabel>{pair.label}</CustomLabel>
                </Grid>
                <Grid item xs={1}>
                  <CustomLabel>:</CustomLabel>
                </Grid>
                <Grid item xs={6}>
                  <CustomValue>{pair.value}</CustomValue>
                </Grid>
              </Grid>
            ))}

            {location["latitude"] && (
              <div style={{ marginTop: "20px" }}>
                <CustomLabel>{t("Farm Map Location")}</CustomLabel>
                <LoadScript
                  googleMapsApiKey={GOOGLE_MAPS_API_KEY}
                  onLoad={() => setIsScriptLoaded(true)}
                >
                  <LocateLand
                    center={{
                      lat: location["latitude"],
                      lng: location["longitude"],
                    }}
                    setCenter={() => {}}
                    path={path}
                    setPath={() => {}}
                    editable={false}
                    setCurrentLocation={() => {}}
                    isScriptLoaded={isScriptLoaded}
                  />
                </LoadScript>
              </div>
            )}

            <Grid item xs={12} style={{ marginTop: "20px" }}>
              <CustomLabel>{t("Legal Document")}:</CustomLabel>
            </Grid>
            <Grid container item xs={9} alignItems="center">
              <Grid item xs={9}>
                <CustomValue>
                  {tokenData.sowing_file ? (
                    <Typography style={{ fontSize: "12px" }}>
                      {tokenData.sowing_file.split("/").pop().split("?")[0]}
                    </Typography>
                  ) : (
                    t("No farm document available")
                  )}
                </CustomValue>
              </Grid>
              {tokenData.sowing_file && (
                <>
                  <Grid item xs={1} columnGap={2} sx={{ marginTop: "10px" }}>
                    <a
                      href={tokenData.sowing_file}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <img src={ViewIcon} alt="View Icon" />
                    </a>
                  </Grid>
                  <Grid
                    item
                    xs={1}
                    sx={{ marginLeft: "20px", marginTop: "10px" }}
                    // onClick={handleDownload}
                  >
                    <img src={DownLoadIcon} alt="Download Icon" />
                  </Grid>
                </>
              )}
            </Grid>

            <a
              href={`/view-crop/IN-RICE-${cropId}`}
              target="_blank"
              rel="noreferrer"
            >
              {t("Get Public URL")}
            </a>
          </Grid>

          <Grid item xs={12} style={{ marginTop: "20px" }}>
            <GenericButton
              onClick={handleFarmTraceActivities}
              backgroundColor="#4CAF50"
              fontColor="white"
              padding="8px 0"
              fontSize="14px"
              hideIcon
            >
              {t("Tracing Activities")}
            </GenericButton>
          </Grid>

          <Grid item xs={12} style={{ marginTop: "20px" }}>
            <GenericButton
              onClick={managePlantation}
              backgroundColor="#4CAF50"
              fontColor="white"
              padding="8px 0"
              fontSize="14px"
              hideIcon
            >
              {t("Sowing Activities")}
              {/* {tokenData.farm_type === "CROP" ? t("Crop") : t("Plantation")} */}
            </GenericButton>
          </Grid>
          {tokenData.farm_type === "CROP" && (
            <Grid item xs={12} style={{ marginTop: "20px" }}>
              <GenericButton
                onClick={handleSell}
                backgroundColor="#4CAF50"
                fontColor="white"
                padding="8px 0"
                fontSize="14px"
                hideIcon
              >
                {selling ? "Listed" : "Sell"}
              </GenericButton>
            </Grid>
          )}
        </Container>
      </LoaderContainer>
    </>
  );
}
